import { useEffect, useState } from "react";
import { fetchContactDetails } from "../api/contacts";
import { FiX, FiMail, FiPhone, FiGlobe, FiMapPin, FiTag, FiClock, FiUser, FiBriefcase } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";

export default function ContactDetailsModal({ contactId, isOpen, onClose }) {
  const { token } = useAuth();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !contactId) return;
    async function loadDetails() {
      try {
        setLoading(true);
        setError("");
        const data = await fetchContactDetails(token.access, contactId);
        setContact(data);
      } catch (err) {
        setError("Failed to load contact details");
      } finally {
        setLoading(false);
      }
    }
    loadDetails();
  }, [isOpen, contactId, token]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Contact Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-gray-300 border-r-gray-900"></div>
                <p className="mt-2 text-sm text-gray-600">Loading contact details...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {!loading && !error && contact && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <FiUser className="text-blue-700" size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{contact.name}</h3>
                    {contact.company_name && (
                      <p className="text-gray-600 mt-1 flex items-center gap-2">
                        <FiBriefcase size={16} />
                        {contact.company_name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {contact.category}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {contact.area}
                  </span>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                  Contact Information
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-sm">
                    <FiMail className="text-gray-400 mt-0.5 flex-shrink-0" size={18} />
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-gray-700 hover:text-blue-600 break-all"
                    >
                      {contact.email}
                    </a>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <FiPhone className="text-gray-400 mt-0.5 flex-shrink-0" size={18} />
                    <a
                      href={`tel:${contact.phone}`}
                      className="text-gray-700 hover:text-blue-600"
                    >
                      {contact.phone}
                    </a>
                  </div>
                  {contact.website && (
                    <div className="flex items-start gap-3 text-sm">
                      <FiGlobe className="text-gray-400 mt-0.5 flex-shrink-0" size={18} />
                      <a
                        href={contact.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:text-blue-700 break-all"
                      >
                        {contact.website}
                      </a>
                    </div>
                  )}
                  {contact.address && (
                    <div className="flex items-start gap-3 text-sm">
                      <FiMapPin className="text-gray-400 mt-0.5 flex-shrink-0" size={18} />
                      <span className="text-gray-700 leading-relaxed">{contact.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Services Offered */}
              {contact.services_offered && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <FiTag size={16} /> Services Offered
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {contact.services_offered}
                    </p>
                  </div>
                </div>
              )}

              {/* Description */}
              {contact.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                    Description
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                      {contact.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Email Status Flags */}
              {(contact.unsubscribed || contact.bounced || contact.complaint) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                    Email Status
                  </h4>
                  <div className="space-y-2">
                    {contact.unsubscribed && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-3">
                        <div className="bg-yellow-100 p-1.5 rounded">
                          <svg className="w-4 h-4 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-yellow-900">Unsubscribed</p>
                          <p className="text-xs text-yellow-700 mt-0.5">This contact has opted out of receiving emails</p>
                        </div>
                      </div>
                    )}

                    {contact.bounced && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                        <div className="bg-red-100 p-1.5 rounded">
                          <svg className="w-4 h-4 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-900">Email Bounced</p>
                          {contact.bounce_reason && (
                            <p className="text-xs text-red-700 mt-0.5">{contact.bounce_reason}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {contact.complaint && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-3">
                        <div className="bg-orange-100 p-1.5 rounded">
                          <svg className="w-4 h-4 text-orange-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-orange-900">Complaint Reported</p>
                          {contact.complaint_reason && (
                            <p className="text-xs text-orange-700 mt-0.5">{contact.complaint_reason}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Footer Meta */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <FiClock size={14} />
                  <span>
                    Added on {new Date(contact.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}